import React, { useRef, useEffect } from "react";
import Clipboard from "@/components/Clipboard";
import Skeleton from "@/components/Skeleton";

export default function CodeServer({ result = "", loading }) {
  const green = useRef(false);
  const outputRef = useRef(null);
  
  // Auto-scroll to bottom when new content arrives for ping/traceroute
  useEffect(() => {
    if (outputRef.current && result.raw) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [result.raw]);

  // Determine if this is a streaming command (ping/traceroute) or BGP
  const isStreamingCommand = result.command?.toLowerCase?.() === 'ping' || 
                             result.command?.toLowerCase?.() === 'traceroute';

  const formatLine = (line) => {
    if (line.trim().startsWith("*>")) {
      const parts = line?.split(/\s+/).filter(Boolean);
      if (parts?.length >= 3) {
        // Find Communities part
        const communitiesStart = line?.indexOf("{Communities:");
        const communitiesEnd = communitiesStart !== -1 ? line?.indexOf("}", communitiesStart) + 1 : -1;
        
        if (communitiesStart !== -1 && communitiesEnd !== -1) {
          // Split the line into three parts: before Next Hop, Next Hop, and after Next Hop
          const nextHopIndex = line?.indexOf(parts[2]);
          const beforeNextHop = line?.substring(0, nextHopIndex);
          const nextHop = parts?.[2];
          const middlePart = line?.substring(nextHopIndex + nextHop.length, communitiesStart);
          const communities = line?.substring(communitiesStart, communitiesEnd);
          const afterCommunities = line?.substring(communitiesEnd);

          return (
            <span>
              {beforeNextHop}
              <span className="text-primary-fixed">{nextHop}</span>
              {middlePart}
              <span className="text-success">{communities}</span>
              {afterCommunities}
            </span>
          );
        } else {
          // If no Communities, just highlight Next Hop
          const nextHopIndex = line?.indexOf(parts[2]);
          const beforeNextHop = line?.substring(0, nextHopIndex);
          const nextHop = parts[2];
          const afterNextHop = line?.substring(nextHopIndex + nextHop.length);

          return (
            <span>
              {beforeNextHop}
              <span className="text-primary-fixed">{nextHop}</span>
              {afterNextHop}
            </span>
          );
        }
      }
    }
    return line;
  };
  return (
    <>
      {result?.["RPKI_STATUS"]?.[0] && (
        <p
          className={`mb-4 p-3 bg-opacity-25 rounded-md   ${
            result["RPKI_STATUS"]?.[0]["rpki status"] === "valid"
              ? " text-success bg-success"
              : " bg-error text-error"
          }`}
        >
          RPKI Status : {result["RPKI_STATUS"][0]["rpki status"] || ""} -
          <span className=" opacity-50 ml-1">
            {result["RPKI_STATUS"][0]["rpki description"]}
          </span>
        </p>
      )}
      {/* {result["command"] && (
        <p className=" pb-4 mb-4 dark:text-dark-neutral-medium text-neutral-medium border-b dark:border-dark-br-low border-br-low">
          Command : {result["command"]}
        </p>
      )} */}
      <div className="flex pb-4 w-full flex-col-reverse gap-6">
        {!loading || result.raw ? (
          <div className="flex result flex-1 overflow-x-auto pb-6">
            {/* Display for BGP data - using raw output */}
            {result["raw output"] && !isStreamingCommand && (
              <div className="font-mono whitespace-pre">
                {result["raw output"]?.split("\n")?.map((line, ind) => {
                  if (
                    line.toLocaleUpperCase()?.trim()?.startsWith("PATH TYPE:") &&
                    line?.toLocaleUpperCase()?.includes("IS BEST")
                  ) {
                    green.current = true;
                  }
                  if (green?.current && line?.trim() === "") {
                    green.current = false;
                  }
                  return line ? (
                    <p
                      className={`font-normal ${
                        green.current
                          ? "text-primary-fixed"
                          : "dark:text-dark-neutral-default text-neutral-default"
                      }`}
                      key={"line" + ind}
                    >
                      {formatLine(line)}
                    </p>
                  ) : (
                    <br key={"line" + ind} />
                  );
                })}
              </div>
            )}
            
            {/* Terminal-like display for ping/traceroute commands */}
            {isStreamingCommand && result['raw'] && (
              <div 
                ref={outputRef}
                className="flex-1 font-mono whitespace-pre overflow-y-auto max-h-[500px] terminal-output"
                style={{ scrollBehavior: 'smooth' }}
              >
                {result['raw'].split('\n').map((line, ind, arr) => (
                  <div 
                    key={`raw-${ind}`}
                    className="terminal-line"
                  >
                    {line}
                    {/* Show blinking cursor at the end of the last line if loading */}
                  </div>
                ))}
             {loading && (
                      <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-blink"></span>
                    )}
              </div>
            )}
            
            {/* For all other cases, including BGP results with raw property */}
            {!isStreamingCommand && result['raw'] && !result["raw output"] && (
              <pre className="flex-1 whitespace-pre break-words">
                {result['raw'].split('\n').map((line, ind) => (
                  <p key={`raw-${ind}`}>
                    {formatLine(line)}
                  </p>
                ))}
              </pre>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-4">
            <Skeleton width={275} height={20} />
            <Skeleton width={375} height={20} />
            <Skeleton width={325} height={20} />
            <Skeleton width={355} height={20} />
          </div>
        )}
        {!loading || result.raw ? (
          <Clipboard text={result["raw"] || result["raw output"]} />
        ) : (
          <Skeleton className={'mb-4'} width={175} height={30} />
        )}
      </div>
    </>
  );
}
